import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const FilterBar = ({ onFilterChange, resultsCount, isLoading, filters }) => {
  const [localFilters, setLocalFilters] = useState(filters || {
    startDate: '',
    endDate: '',
    status: 'all',
    sortBy: 'date-desc',
    documentType: 'all'
  });
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const documentTypeOptions = [
    { value: 'all', label: 'All Documents' },
    { value: 'rotation', label: 'Rotations Only' },
    { value: 'timecard', label: 'Timecards Only' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'verified', label: 'Verified' },
    { value: 'pending', label: 'Pending Review' },
    { value: 'discrepancy', label: 'Discrepancy Found' },
    { value: 'compliant', label: 'FAR Compliant' },
    { value: 'violation', label: 'FAR Violation' }
  ];

  const sortOptions = [
    { value: 'date-desc', label: 'Newest First' },
    { value: 'date-asc', label: 'Oldest First' },
    { value: 'hours-desc', label: 'Most Hours' },
    { value: 'hours-asc', label: 'Least Hours' },
    { value: 'identifier-asc', label: 'Identifier (A-Z)' },
    { value: 'identifier-desc', label: 'Identifier (Z-A)' }
  ];

  const handleFilterChange = (field, value) => {
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      startDate: '',
      endDate: '',
      status: 'all',
      sortBy: 'date-desc',
      documentType: 'all'
    };
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const hasActiveFilters = localFilters?.startDate || localFilters?.endDate || 
                           localFilters?.status !== 'all' || localFilters?.documentType !== 'all';

  return (
    <>
      <button
        className="mobile-menu-button lg:hidden"
        style={{ top: '80px', left: '16px' }}
        onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
        aria-label="Toggle filter panel"
      >
        <Icon 
          name={isMobileFilterOpen ? 'X' : 'Filter'} 
          size={24} 
          color="var(--color-accent-cyan)" 
        />
      </button>
      {isMobileFilterOpen && (
        <div 
          className="overlay"
          onClick={() => setIsMobileFilterOpen(false)}
        />
      )}
      <div className={`
        fixed lg:relative top-0 left-0 h-full lg:h-auto w-80 lg:w-full
        bg-secondary lg:bg-transparent border-r lg:border-r-0 border-accent-cyan/20
        transition-transform duration-300 z-[950] lg:z-auto
        ${isMobileFilterOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-4 lg:p-0">
          <div className="flex items-center justify-between mb-6 lg:hidden">
            <h3 className="text-lg font-semibold text-foreground">Filters</h3>
            <button
              onClick={() => setIsMobileFilterOpen(false)}
              className="p-2 rounded-lg hover:bg-tertiary transition-smooth"
              aria-label="Close filter panel"
            >
              <Icon name="X" size={20} color="var(--color-muted-foreground)" />
            </button>
          </div>

          <div className="card p-4 md:p-6">
            {/* Document Type Toggle - Prominent Position */}
            <div className="mb-6 pb-6 border-b border-accent-cyan/20">
              <label className="block text-sm font-medium text-foreground mb-3">
                Document Type
              </label>
              <Select
                value={localFilters?.documentType}
                onChange={(e) => handleFilterChange('documentType', e?.target?.value)}
                options={documentTypeOptions}
                className="w-full"
              />
            </div>

            <div className="flex flex-col lg:flex-row gap-4 mb-4">
              <div className="flex-1 min-w-0">
                <Input
                  type="date"
                  label="Start Date"
                  value={localFilters?.startDate}
                  onChange={(e) => handleFilterChange('startDate', e?.target?.value)}
                  className="w-full"
                />
              </div>
              <div className="flex-1 min-w-0">
                <Input
                  type="date"
                  label="End Date"
                  value={localFilters?.endDate}
                  onChange={(e) => handleFilterChange('endDate', e?.target?.value)}
                  className="w-full"
                />
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-4 mb-4">
              <div className="flex-1 min-w-0">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Status
                </label>
                <Select
                  value={localFilters?.status}
                  onChange={(e) => handleFilterChange('status', e?.target?.value)}
                  options={statusOptions}
                  className="w-full"
                />
              </div>
              <div className="flex-1 min-w-0">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Sort By
                </label>
                <Select
                  value={localFilters?.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e?.target?.value)}
                  options={sortOptions}
                  className="w-full"
                />
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-accent-cyan/10">
              <div className="text-sm text-muted-foreground">
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <Icon name="Loader2" className="w-4 h-4 animate-spin" />
                    Loading...
                  </span>
                ) : (
                  <span>
                    Showing <span className="font-semibold text-accent-cyan">{resultsCount}</span> results
                  </span>
                )}
              </div>
              {hasActiveFilters && (
                <Button
                  variant="secondary"
                  iconName="X"
                  iconPosition="left"
                  onClick={handleClearFilters}
                  className="w-full sm:w-auto"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterBar;
